import React, { useState } from "react";
import { Formik } from "formik";
import { Redirect, Link } from "react-router-dom";
import { injectIntl } from "react-intl";
import { LockClosedIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import changePassword from "./resetPasswordPetition";
import * as qs from "query-string";

function ResetPassword(props) {
  const { intl } = props;
  const parsed = qs.parse(props.location.search);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  if (isSuccess) {
    return <Redirect to="/auth/login" />;
  }

  if (!parsed.token) {
    return <Redirect to="/auth/login" />;
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-flex tw-overflow-hidden">
      {/* Left Side - Background Image with Logo and Text */}
      <div
        className="tw-hidden lg:tw-flex tw-flex-col tw-justify-between tw-w-1/2 tw-bg-cover tw-bg-center tw-bg-no-repeat tw-p-12 tw-relative"
        style={{
          backgroundImage: 'url(/media/bg/logindsa.jpg)',
        }}
      >
        {/* Dark overlay */}
        <div className="tw-absolute tw-inset-0 tw-bg-black/40"></div>

        {/* Content */}
        <div className="tw-relative tw-z-10">
          <img
            src="/media/logos/logo-4.png"
            alt="ITP Logo"
            style={{ width: '100px' }}
            className="tw-mb-8"
          />
        </div>

        <div className="tw-relative tw-z-10">
          <h3 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-4">
            Welcome to ITP!
          </h3>
          <p className="tw-text-white/90 tw-text-lg tw-leading-relaxed">
            By logging in you agree to follow all ITP company policies and procedures. Your activity may be monitored.
          </p>
        </div>

        <div className="tw-relative tw-z-10">
          <p className="tw-text-white/80 tw-text-sm">
            ITP © {new Date().getFullYear()}
          </p>
        </div>
      </div>

      {/* Right Side - Reset Password Form */}
      <div className="tw-w-full lg:tw-w-1/2 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-blue-600 tw-to-indigo-700 dark:tw-from-gray-900 dark:tw-to-gray-800 tw-overflow-auto">
        <div className="tw-max-w-md tw-w-full tw-px-8 tw-py-12">
          {/* Logo - always visible */}
          <div className="tw-text-center tw-mb-8">
            <img
              src="/media/logos/logo-4.png"
              alt="ITP Logo"
              style={{ width: '100px' }}
              className="tw-mx-auto tw-mb-4"
            />
            <h2 className="tw-text-2xl tw-font-bold tw-text-white">
              Reset Password
            </h2>
            <p className="tw-mt-2 tw-text-sm tw-text-white/90 dark:tw-text-gray-300">
              Enter your new password
            </p>
          </div>

          {/* Reset Password Card */}
          <div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-xl tw-p-8">
            <Formik
              initialValues={{
                password: "",
                confirmPassword: "",
              }}
              validate={(values) => {
                const errors = {};

                if (!values.password) {
                  errors.password = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                }

                if (!values.confirmPassword) {
                  errors.confirmPassword = intl.formatMessage({
                    id: "AUTH.VALIDATION.REQUIRED_FIELD",
                  });
                } else if (values.password !== values.confirmPassword) {
                  errors.confirmPassword = "Password and Confirm Password didn't match.";
                }

                return errors;
              }}
              onSubmit={(values, { setStatus, setSubmitting }) => {
                setLoading(true);
                setErrorMessage("");
                setSuccessMessage("");

                changePassword(parsed.token, values.password)
                  .then(() => {
                    setLoading(false);
                    setSuccessMessage("Your password has been changed successfully!");
                    setTimeout(() => {
                      setIsSuccess(true);
                    }, 2000);
                  })
                  .catch(() => {
                    setLoading(false);
                    setSubmitting(false);
                    setErrorMessage("Error changing password. The reset link may have expired.");
                  });
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit} className="tw-space-y-6">
                  {/* Error Alert */}
                  {errorMessage && (
                    <div className="tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
                      <ExclamationCircleIcon className="tw-h-5 tw-w-5 tw-text-red-600 dark:tw-text-red-400 tw-flex-shrink-0 tw-mt-0.5" />
                      <div>
                        <h3 className="tw-text-sm tw-font-medium tw-text-red-800 dark:tw-text-red-300">
                          Error
                        </h3>
                        <p className="tw-mt-1 tw-text-sm tw-text-red-700 dark:tw-text-red-400">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Success Alert */}
                  {successMessage && (
                    <div className="tw-bg-green-50 dark:tw-bg-green-900/20 tw-border tw-border-green-200 dark:tw-border-green-800 tw-rounded-lg tw-p-4 tw-flex tw-items-start tw-gap-3">
                      <CheckCircleIcon className="tw-h-5 tw-w-5 tw-text-green-600 dark:tw-text-green-400 tw-flex-shrink-0 tw-mt-0.5" />
                      <div>
                        <h3 className="tw-text-sm tw-font-medium tw-text-green-800 dark:tw-text-green-300">
                          Success
                        </h3>
                        <p className="tw-mt-1 tw-text-sm tw-text-green-700 dark:tw-text-green-400">
                          {successMessage}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Password Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      New Password
                    </label>
                    <div className="tw-relative">
                      <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                        <LockClosedIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                      </div>
                      <input
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                          touched.password && errors.password
                            ? "tw-border-red-300 dark:tw-border-red-700"
                            : "tw-border-gray-300 dark:tw-border-gray-600"
                        }`}
                        placeholder="••••••••"
                      />
                    </div>
                    {touched.password && errors.password && (
                      <p className="tw-mt-2 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Confirm Password
                    </label>
                    <div className="tw-relative">
                      <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none">
                        <LockClosedIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                      </div>
                      <input
                        type="password"
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-3 tw-border tw-rounded-lg tw-bg-white dark:tw-bg-gray-700 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-transition-colors ${
                          touched.confirmPassword && errors.confirmPassword
                            ? "tw-border-red-300 dark:tw-border-red-700"
                            : "tw-border-gray-300 dark:tw-border-gray-600"
                        }`}
                        placeholder="••••••••"
                      />
                    </div>
                    {touched.confirmPassword && errors.confirmPassword && (
                      <p className="tw-mt-2 tw-text-sm tw-text-red-600 dark:tw-text-red-400">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <button
                      type="submit"
                      disabled={isSubmitting || loading}
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-font-semibold tw-shadow-lg hover:tw-shadow-xl tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="tw-animate-spin tw-h-5 tw-w-5 tw-text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="tw-opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="tw-opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Resetting...</span>
                        </>
                      ) : (
                        <span>Reset Password</span>
                      )}
                    </button>

                    <Link
                      to="/auth/login"
                      className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-px-4 tw-py-3 tw-rounded-lg tw-bg-gray-100 hover:tw-bg-gray-200 dark:tw-bg-gray-700 dark:hover:tw-bg-gray-600 tw-text-gray-700 dark:tw-text-gray-300 tw-font-semibold tw-transition-all"
                    >
                      <ArrowLeftIcon className="tw-h-5 tw-w-5" />
                      <span>Back to Login</span>
                    </Link>
                  </div>
                </form>
              )}
            </Formik>
          </div>

          {/* Footer */}
          <p className="tw-mt-6 tw-text-center tw-text-sm tw-text-white/80 dark:tw-text-gray-400 lg:tw-hidden">
            ITP © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
}

export default injectIntl(ResetPassword);
